<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import { helpers, required } from '@vuelidate/validators';
import { alphanumericUnderscore } from '../helpers/rulesHelper';
import useVuelidate from '@vuelidate/core';

import { OnClickOutside } from '@vueuse/components';
import DropdownMenu from 'next/dropdown-menu/DropdownMenu.vue';
import Spinner from 'next/spinner/Spinner.vue';
import Button from 'next/button/Button.vue';
import Breadcrumb from 'next/breadcrumb/Breadcrumb.vue';
import Editor from 'next/wgpt/Automation/editor/Index.vue';
import ToolEditorLayout from './ToolEditorLayout.vue';
import ToolEditorSettings from './ToolEditorSettings.vue';
import TestToolDialog from '../test/TestToolDialog.vue';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();
const route = useRoute();
const router = useRouter();

const botId = computed(() => Number(route.params.botId));
const bot = computed(() => store.getters['wgptBots/getBot'](botId.value));
const testToolDialogRef = ref(null);

const showActionMenu = ref(false);

const toolSetId = computed(() => {
  return Number(route.params.toolSetId);
});
const toolId = computed(() => {
  return Number(route.params.toolId);
});
const tools = computed(() => {
  return getters['wgptBotTools/getTools'].value(toolSetId.value);
});
const toolSet = computed(() => {
  return getters['wgptBotTools/getToolSet'].value(toolSetId.value);
});
const tool = computed(() => {
  return getters['wgptBotTools/getTool'].value(toolSetId.value, toolId.value);
});
const uiFlags = useMapGetter('wgptBotTools/getUIFlags');

const isUniqueName = value => {
  if (!value) return true;
  return !tools.value.some(
    ({ name: toolName, id }) => toolName === value && id !== toolId.value
  );
};

const initialState = {};
const state = reactive({ ...initialState });
const rules = {
  value: {
    name: {
      required,
      alphanumericUnderscore,
      isUnique: helpers.withMessage(
        () => t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.FORM.NAME.ERROR_UNIQUE'),
        isUniqueName
      ),
    },
    description: {
      required,
    },
  },
};
const v$ = useVuelidate(rules, state);

const hasPendingChanges = computed(() => {
  if (!tool.value) return false;

  return (
    state.value.name !== tool.value.name ||
    state.value.description !== tool.value.description ||
    JSON.stringify(state.value.steps) !== JSON.stringify(tool.value.steps) ||
    JSON.stringify(state.value.trigger) !== JSON.stringify(tool.value.trigger)
  );
});

const isLoading = computed(() => {
  if (tool.value.id !== toolId.value) return true;
  if (toolSet.value.id !== toolSetId.value) return true;
  return false;
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: bot.value?.name || '...',
    },
    {
      label: t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.BREADCRUMB.TOOLS'),
      link: '#',
    },
  ];
  if (toolSet.value?.name) {
    items.push({
      label: toolSet.value.name,
      link: '#',
    });
  }
  if (tool.value?.name) {
    items.push({
      label: tool.value.name,
    });
  }
  return items;
});

const selected = computed(() => {
  return route.query.selected;
});

const settingsHasError = computed(() => {
  return v$.value.$errors.some(error =>
    ['name', 'description'].includes(error.$property)
  );
});

const isToolActive = computed(() => {
  return tool.value?.active;
});

const actionMenuItems = computed(() => {
  return [
    {
      label: t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.HEADER.DEACTIVATE_BUTTON'),
      value: 'deactivate',
      action: 'deactivate',
      disabled:
        isLoading.value ||
        uiFlags.value.isUpdating ||
        hasPendingChanges.value ||
        !isToolActive.value,
    },
    {
      label: t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.HEADER.TEST_BUTTON'),
      value: 'test',
      action: 'test',
      disabled: uiFlags.value.isUpdating || hasPendingChanges.value,
    },
  ];
});

const handleSaveTool = async () => {
  try {
    if (v$.value.$invalid) {
      v$.value.$touch();
      throw new Error(t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.API.ERROR_MESSAGE'));
    }

    const updateData = {
      botId: botId.value,
      toolSetId: toolSetId.value,
      id: toolId.value,
      name: state.value.name,
      description: state.value.description,
      trigger: state.value.trigger,
      steps: state.value.steps,
    };
    const updatedTool = await store.dispatch(
      'wgptBotTools/updateTool',
      updateData
    );
    useAlert(t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.API.SUCCESS_MESSAGE'));

    if (updatedTool) {
      state.value = {
        name: updatedTool.name,
        description: updatedTool.description,
        trigger: updatedTool.trigger,
        steps: updatedTool.steps,
      };
    }
  } catch (error) {
    useAlert(
      error?.message || t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.API.ERROR_MESSAGE')
    );
  }
};

const handleTestTool = async () => {
  if (testToolDialogRef.value) {
    testToolDialogRef.value.open(state.value);
  }
};

const toggleActivate = async isActive => {
  if (isToolActive.value === isActive) return;

  try {
    const updateData = {
      botId: botId.value,
      toolSetId: toolSetId.value,
      id: toolId.value,
      active: isActive,
    };
    await store.dispatch('wgptBotTools/updateTool', updateData);

    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    const successMessage = t(
      `WGPT_BOTS.EDIT.TABS.TOOLS.EDIT_TOOL.API.${isActive ? 'ACTIVATED' : 'DEACTIVATED'}_SUCCESSFUL`
    );
    useAlert(successMessage);
  } catch (error) {
    const errorMessage =
      error.message || t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleMenuAction = ({ action }) => {
  showActionMenu.value = false;
  if (action === 'deactivate') {
    toggleActivate(false);
  }
  if (action === 'test') {
    handleTestTool();
  }
};

watch(
  tool,
  (newTool, oldTool = {}) => {
    if (Object.keys(oldTool || {}).length) return;
    state.value = {
      name: newTool.name,
      description: newTool.description,
      trigger: newTool.trigger,
      steps: newTool.steps,
    };
  },
  { immediate: true }
);

watch(selected, () => {
  v$.value.$touch();
});

const goToBotToolsList = () => {
  router.replace({
    name: 'wgpt_bots_edit',
    params: {
      ...route.params,
      tab: 'toolsets',
    },
    query: {
      expandedToolSet: toolSetId.value,
    },
  });
};

onMounted(() => {
  store.dispatch('wgptBots/show', botId.value);
  if (toolSet.value.id !== toolSetId.value) {
    store.dispatch('wgptBotTools/show', {
      botId: botId.value,
      id: toolSetId.value,
    });
  }
});
</script>

<template>
  <ToolEditorLayout>
    <template #start>
      <div class="flex items-center gap-5">
        <Breadcrumb :items="breadcrumbItems" @click="goToBotToolsList" />
      </div>
    </template>
    <template #end>
      <Button
        :label="t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.HEADER.SAVE_BUTTON')"
        :is-loading="uiFlags.isUpdating"
        :disabled="!hasPendingChanges || uiFlags.isUpdating"
        size="sm"
        class="group-hover/campaign-button:brightness-110"
        @click="handleSaveTool"
      />
      <div class="flex items-center">
        <Button
          :label="t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.HEADER.ACTIVATE_BUTTON')"
          size="sm"
          class="ltr:rounded-r-none rtl:rounded-l-none"
          :disabled="
            isLoading || uiFlags.isUpdating || hasPendingChanges || isToolActive
          "
          @click="toggleActivate(true)"
        />
        <div class="relative">
          <OnClickOutside @trigger="showActionMenu = false">
            <Button
              icon="i-lucide-chevron-down"
              size="sm"
              class="ltr:rounded-l-none rtl:rounded-r-none"
              @click.stop="showActionMenu = !showActionMenu"
            />
            <DropdownMenu
              v-if="showActionMenu"
              :menu-items="actionMenuItems"
              class="mt-2 ltr:right-0 rtl:left-0 top-full"
              @action="handleMenuAction($event)"
            />
          </OnClickOutside>
        </div>
      </div>

      <router-link
        :to="{ query: { selected: 'settings' } }"
        :class="{
          'settings-button-active': selected === 'settings',
          'settings-button-invalid': settingsHasError,
        }"
        replace
      >
        <Button
          variant="ghost"
          :color="settingsHasError ? 'ruby' : 'slate'"
          icon="i-lucide-bolt"
          size="sm"
          class="settings-button"
          :title="t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.HEADER.SETTINGS_BUTTON')"
        />
      </router-link>
    </template>
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <Editor
      v-else
      v-model="state.value"
      :dirty="hasPendingChanges"
      class="h-full border-t border-n-weak"
      disable-trigger
    >
      <template #customSidebarSettings>
        <ToolEditorSettings
          v-model="state.value"
          :tool-set="toolSet"
          :tool="tool"
        />
      </template>
    </Editor>

    <TestToolDialog
      ref="testToolDialogRef"
      :bot-id="botId"
      :tool-set="toolSet"
    />
  </ToolEditorLayout>
</template>

<style lang="scss" scoped>
.settings-button-active .settings-button {
  @apply outline-none outline-n-brand outline-offset-1;
}
.settings-button-invalid .settings-button {
  @apply outline-none outline-n-ruby-10 outline-offset-1;
}
</style>
